import { IAjaxCreationMethod } from '@microsoft/portal-app/lib/auth';
import { addAjaxEpic } from '@microsoft/portal-app/lib/redux/createAjaxEpic';
import { getTenantId } from '@microsoft/portal-app/lib/User/getTenantId';
import { IRootUserState } from '@microsoft/portal-app/lib/User/models';
import { Observable } from 'rxjs';
import {
  getUserItemInsightsSettingsReq,
  getOrgItemInsightsSettingsReq,
  postCheckMemberGroupsReq,
} from '../../../redux/modules/graphApis';
import { IPrivacyAction } from '../../models/IPrivacyActions';
import { IPrivacyState } from '../../models/IPrivacyState';
import { PrivacyActions } from '../../models/PrivacyActions';
import { privacyRegistry as registry } from '../privacyRegistry';
import { IGetUserItemInsightsSettingsResponse } from './IUserItemInsightsSettingsResponse';
import { IGetOrgItemInsightsSettingsResponse } from './IOrgItemInsightsSettingsResponse';
import { map, mergeMap } from 'rxjs/operators';
import { ICheckMemberGroupsResponse } from './ICheckMemberGroupsResponse';
import { ICheckMemberGroupsRequest } from './ICheckMemberGroupsRequest';

type UnwrapObservable<T> = T extends Observable<infer U> ? U : T;

export const getItemInsightsStatusResponse = (
  state: IPrivacyState,
  action: IPrivacyAction<UnwrapObservable<ReturnType<typeof getItemInsightsStatus>>>,
): IPrivacyState => {
  const payload = action.payload;
  if (payload) {
    const [{ isEnabled: userLevelEnabled }, { isEnabledInOrganization: orgLevelEnabled }] = payload;

    let itemInsightsServiceStateValue: IPrivacyState['itemInsightsServiceState']['value'] = 'Enabled';
    if (!orgLevelEnabled) {
      itemInsightsServiceStateValue = 'TenantOptedOut';
    } else if (!userLevelEnabled) {
      itemInsightsServiceStateValue = 'UserOptedOut';
    }
    const nextState: IPrivacyState = {
      ...state,
      itemInsightsServiceState: {
        ...state.itemInsightsServiceState,
        value: itemInsightsServiceStateValue,
      },
    };

    return nextState;
  }
  return state;
};

export const getItemInsightsStatusFailed = (state: IPrivacyState): IPrivacyState => {
  return state;
};

export const getItemInsightsStatus = (ajax: IAjaxCreationMethod, _action: IPrivacyAction, state: IRootUserState) => {
  const tenantId = getTenantId(state) as string;
  const userSettings = ajax.getJSON<IGetUserItemInsightsSettingsResponse>(getUserItemInsightsSettingsReq()).pipe();
  const orgSettings = ajax.getJSON<IGetOrgItemInsightsSettingsResponse>(getOrgItemInsightsSettingsReq(tenantId)).pipe(
    mergeMap((orgSettingsResponse) => {
      // If Tenant is using DisabledInGroup, we need to check if the user is included in disabled group
      if (orgSettingsResponse.disabledForGroup) {
        return ajax
          .postJSON<ICheckMemberGroupsResponse, ICheckMemberGroupsRequest>(
            postCheckMemberGroupsReq({ groupIds: [orgSettingsResponse.disabledForGroup] }),
          )
          .pipe(
            map((checkMembersResponse) => {
              const userIsInDisabledGroup = checkMembersResponse.value.length;
              return userIsInDisabledGroup ? { isEnabledInOrganization: false } : orgSettingsResponse;
            }),
          );
      }

      return Observable.from([orgSettingsResponse]);
    }),
  );
  return userSettings.zip(orgSettings).pipe();
};

addAjaxEpic(registry, PrivacyActions.getItemInsightsStatus, getItemInsightsStatus, {
  statePropertyName: 'itemInsightsServiceState',
  loadingFailedReducer: getItemInsightsStatusFailed,
  loadingSuccessReducer: getItemInsightsStatusResponse,
});
